import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button, Stack } from '@mui/material';
import { ClearAll } from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from '../../store/hooks';
import { hideLongText } from '../../utils/formatters';
import { createNivoTheme, generateColorScheme } from '../../lib/nivoTheme';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { BaseChartControls } from './BaseChartControls';
import { ChartDownloadMenu } from './ChartDownloadMenu';
import { formatExportTimestamp, type ChartExcelData } from '../../utils/chartExcel';
import { useChartExcelContext } from '../../hooks';
import { seriesToRows } from '../../utils/line-chart-excel-rows';


interface NivoChartData {
	series: Array<{
		id: string | number;
		label?: string;
		data: Array<{ x: string | number; y: number }>;
	}>;
	filters: {
		years: number[];
		entities: Array<{ code: string; name: string; groupName?: string }>;
	};
}

// `reduxYears` defaults to `[]` before the filters slice is populated. Without
// this guard, Math.min(...[]) / Math.max(...[]) return Infinity / -Infinity,
// which become the xScale bounds. Effect ordering happens to populate
// `reduxYears` before this ever renders today, but that's coincidence, not a
// guarantee.
export const computeYearAxisBounds = (years: number[]): { min: number; max: number } => {
	if (years.length === 0) return { min: 0, max: 1 };
	return { min: Math.min(...years), max: Math.max(...years) };
};

export interface BaseEntityLineChartProps {
	height?: number;
	title: string;
	downloadFilename: string;
	entityLabel: string;
	entityPlaceholder: string;
	groupByEntity?: boolean;
	nivoData: NivoChartData | null;
	isLoading: boolean;
	error: Error | null;
	legendMarginRight?: number;
}

export function BaseEntityLineChart({
	height = 400,
	title,
	downloadFilename,
	entityLabel,
	entityPlaceholder,
	groupByEntity = false,
	nivoData,
	isLoading,
	error,
	legendMarginRight = 250,
}: BaseEntityLineChartProps) {
	const chartRef = useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const reduxYears = useAppSelector((state) => state.filters.years);
	const [selectedYears, setSelectedYears] = useState<string[]>(reduxYears.map(y => String(y)));
	const [selectedEntities, setSelectedEntities] = useState<string[]>([]);

	const { min: minX, max: maxX } = useMemo(() => computeYearAxisBounds(reduxYears), [reduxYears]);

	const filteredChartData = useMemo(() => {
		if (!nivoData) return null;

		let filteredSeries = nivoData.series;

		if (selectedYears.length > 0) {
			filteredSeries = filteredSeries.map(series => ({
				...series,
				data: series.data.filter(point => selectedYears.includes(String(point.x)))
			}));
		}

		if (selectedEntities.length > 0) {
			filteredSeries = filteredSeries.filter(s => selectedEntities.includes(s.id as string));
		}

		return { ...nivoData, series: filteredSeries };
	}, [nivoData, selectedEntities, selectedYears]);

	const handleClearFilters = useCallback(() => {
		setSelectedYears(reduxYears.map(y => String(y)));
		setSelectedEntities([]);
	}, [reduxYears]);

	const handleDownloadPNG = useCallback(async () => {
		if (!chartRef.current) return;

		try {
			const canvas = await html2canvas(chartRef.current);
			const link = document.createElement('a');
			link.download = `${downloadFilename}-${formatExportTimestamp()}.png`;
			link.href = canvas.toDataURL();
			link.click();
		} catch (err) {
			console.error('Error downloading chart:', err);
		}
	}, [downloadFilename]);

	const yearsLabel = useMemo(() => {
		return [...selectedYears]
			.map(Number)
			.sort((a, b) => a - b)
			.join(', ');
	}, [selectedYears]);

	const baseExcelContext = useChartExcelContext(yearsLabel || undefined);

	const excelContext = useMemo(() => ({
		...baseExcelContext,
		extra: {
			[entityLabel]: filteredChartData
				? filteredChartData.series.map((s) => s.label ?? String(s.id)).join(', ')
				: '',
		},
	}), [baseExcelContext, filteredChartData, entityLabel]);

	const excelData: ChartExcelData | undefined = useMemo(() => {
		if (!filteredChartData || filteredChartData.series.length === 0) return undefined;
		if (selectedEntities.length === 0 || selectedYears.length === 0) return undefined;

		return {
			sheetName: title,
			rows: seriesToRows(filteredChartData.series),
			context: excelContext,
		};
	}, [filteredChartData, selectedEntities, selectedYears, title, excelContext]);

	const availableYears = useMemo(() => {
		return nivoData?.filters?.years?.map(y => String(y)) || [];
	}, [nivoData]);

	const availableEntities = useMemo(() => {
		return nivoData?.filters?.entities || [];
	}, [nivoData]);

	const nivoTheme = useMemo(() => createNivoTheme(theme), [theme]);

	const formattedNivoData = useMemo(() => {
		if (!filteredChartData || filteredChartData.series.length === 0) return [];

		const colors = generateColorScheme(filteredChartData.series.length, theme);

		return filteredChartData.series.map((series, idx) => ({
			id: hideLongText(series.label || String(series.id) || `series-${idx}`, 30),
			color: colors[idx],
			data: series.data
		}));
	}, [filteredChartData, theme]);

	useEffect(() => {
		if (reduxYears.length > 0 && selectedYears.length === 0) {
			setSelectedYears(reduxYears.map(y => String(y)));
		}
	}, [reduxYears, selectedYears.length]);

	useEffect(() => {
		if (availableYears.length > 0 && selectedYears.length === 0) {
			setSelectedYears(availableYears);
		}
	}, [availableYears, selectedYears.length]);

	if (isLoading) {
		return (
			<Card>
				<CardContent>
					<Box display="flex" justifyContent="center" alignItems="center" height={200}>
						<CircularProgress />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Alert severity="error">
						Có lỗi xảy ra khi tải dữ liệu biểu đồ
					</Alert>
				</CardContent>
			</Card>
		);
	}

	if (!nivoData) {
		return (
			<Card>
				<CardContent>
					<Alert severity="info">
						Chưa có dữ liệu
					</Alert>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<Box sx={{ mb: 3 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6" component="h2">
							{title}
						</Typography>
						<Stack direction="row" spacing={1}>
							<Button
								variant="outlined"
								size="small"
								startIcon={<ClearAll />}
								onClick={handleClearFilters}
							>
								Xóa bộ lọc
							</Button>
							<ChartDownloadMenu
								variant="button"
								onDownloadPng={handleDownloadPNG}
								excel={excelData}
								excelFilename={downloadFilename}
							/>
						</Stack>
					</Stack>

					<BaseChartControls
						availableYears={availableYears}
						availableEntities={availableEntities}
						selectedYears={selectedYears}
						selectedEntities={selectedEntities}
						onYearsChange={setSelectedYears}
						onEntitiesChange={setSelectedEntities}
						entityLabel={entityLabel}
						entityPlaceholder={entityPlaceholder}
						groupByEntity={groupByEntity}
					/>
				</Box>

				<Box ref={chartRef} sx={{ width: '100%', height: height }}>
					{selectedEntities.length > 0 && selectedYears.length > 0 ? (
						<ResponsiveLine
							data={formattedNivoData}
							margin={{ top: 70, right: legendMarginRight, bottom: 70, left: 70 }}
							xScale={{ type: 'linear', min: minX - 0.1, max: maxX + 0.1 }}
							yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
							axisBottom={{
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 0,
								tickValues: reduxYears,
								legend: 'Năm',
								legendOffset: 60,
								legendPosition: 'middle',
							}}
							axisLeft={{
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 0,
								legend: 'Số lượng',
								legendOffset: -60,
								legendPosition: 'middle',
								format: (value) => Math.round(value).toString(),
							}}
							enablePoints={true}
							enablePointLabel={true}
							pointLabel={(point) => Math.round(point.data.y as number).toString()}
							pointLabelYOffset={-12}
							pointSize={8}
							pointBorderWidth={2}
							pointBorderColor={{ from: 'serieColor' }}
							enableArea={false}
							animate={false}
							legends={[
								{
									anchor: 'right',
									direction: 'column',
									justify: false,
									translateX: 275,
									itemsSpacing: 10,
									itemDirection: 'left-to-right',
									itemWidth: 250,
									itemHeight: 20,
									itemOpacity: 0.75,
									symbolSize: 12,
									symbolShape: 'circle',
								},
							]}
							theme={nivoTheme}
						/>
					) : (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<Typography variant="body1" color="text.secondary">
								Vui lòng chọn đầy đủ thông tin để hiển thị biểu đồ
							</Typography>
						</Box>
					)}
				</Box>
			</CardContent>
		</Card>
	);
}
